import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
  mintPassport,
  fetchTicketThumbnail,
  lookupWatchesByReference,
  type CustomAttribute,
  type MintPassportFields,
  type WatchSuggestion,
} from "@/lib/mint";
import { GENDERS, buildPassportName } from "@/lib/passportName";
import type { TicketAttachment } from "@/lib/dppMutations";
import { apiErrorMessage } from "@/lib/apiError";
import { reportError } from "@/observability";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dropzone } from "@/components/ui/dropzone";
import {
  RemoteThumbnail,
  SortableTile,
  Tile,
  coverStyle,
  gridStyle,
} from "@/components/dpp/imageTiles";

const DEPARTMENTS: { value: string; label: string }[] = [
  { value: "phigora", label: "Phigora" },
  { value: "watchcentral", label: "Watch Central" },
];

const SPEC_FIELDS = [
  { type: "Dial Color", placeholder: "e.g. Black" },
  { type: "Case Material", placeholder: "e.g. Stainless Steel" },
  { type: "Bracelet Material", placeholder: "e.g. Rubber" },
] as const;

type SpecType = (typeof SPEC_FIELDS)[number]["type"];

export interface MintSerialOption {
  serialNumber: string;
  minted: boolean;
}

export interface MintPrefill {
  brand?: string;
  model?: string;
  serialNumber?: string;
  reference?: string;
  gender?: string;
  description?: string;
  customAttributes?: CustomAttribute[];
  billNumber?: string;
  itemId?: string;
  serialOptions?: MintSerialOption[];
  attachments?: TicketAttachment[];
  sourceLabel?: string;
}

type PassportImage =
  | { kind: "ticket"; key: string; attachment: TicketAttachment }
  | { kind: "upload"; key: string; file: File; previewUrl: string };

type FieldKey = "brand" | "model" | "serial" | "department" | "eventDate";

function RequiredMark() {
  return (
    <span style={{ color: "var(--danger)", marginLeft: 4 }} aria-hidden>
      *
    </span>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 11,
        color: "var(--danger)",
      }}
    >
      {message}
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function acquisitionTitle(department: string): string {
  return department === "watchcentral"
    ? "Acquired by Watch Central"
    : "Acquired by Phigora";
}

function ticketKey(a: TicketAttachment): string {
  return `ticket:${a.ticketId}:${a.attachmentId}`;
}

function specsFrom(attributes: CustomAttribute[]): Record<SpecType, string> {
  const specs = {} as Record<SpecType, string>;
  for (const { type } of SPEC_FIELDS) {
    specs[type] =
      attributes.find((a) => a.type.toLowerCase() === type.toLowerCase())
        ?.value ?? "";
  }
  return specs;
}

const monoNote: React.CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 11,
  color: "var(--ink-mute)",
};

const MIN_REFERENCE_LENGTH = 3;

const CATALOGUE_FIELDS = [
  "brand",
  "model",
  "Dial Color",
  "Case Material",
] as const;

function normalizeReference(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function watchTitle(watch: WatchSuggestion): string {
  return (
    [watch.brand, watch.model].filter(Boolean).join(" · ") || watch.name || "—"
  );
}

function watchDetail(watch: WatchSuggestion): string {
  return [
    watch.reference,
    watch.year,
    watch.dialColor,
    watch.caseMaterial,
    watch.caseDiameter,
  ]
    .filter(Boolean)
    .join(" · ");
}

interface MintPassportFormProps {
  prefill?: MintPrefill;
  manualEntry?: boolean;
  onMinted: () => void;
}

export function MintPassportForm({
  prefill = {},
  manualEntry = false,
  onMinted,
}: MintPassportFormProps) {
  const [brand, setBrand] = useState(prefill.brand ?? "");
  const [model, setModel] = useState(prefill.model ?? "");
  const [serial, setSerial] = useState(prefill.serialNumber ?? "");
  const [reference, setReference] = useState(prefill.reference ?? "");
  const [gender, setGender] = useState(prefill.gender ?? "");
  const [name, setName] = useState("");
  const [nameEdited, setNameEdited] = useState(false);
  const [description, setDescription] = useState(prefill.description ?? "");
  const [specs, setSpecs] = useState<Record<SpecType, string>>(() =>
    specsFrom(prefill.customAttributes ?? [])
  );
  const [department, setDepartment] = useState("");
  const [eventDate, setEventDate] = useState(today);
  const [customerEmail, setCustomerEmail] = useState("");
  const [images, setImages] = useState<PassportImage[]>([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [debouncedReference, setDebouncedReference] = useState("");
  const [watchMatches, setWatchMatches] = useState<WatchSuggestion[]>([]);
  const [matchesOpen, setMatchesOpen] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchNote, setWatchNote] = useState("");
  const [watchError, setWatchError] = useState("");
  const [watchSearched, setWatchSearched] = useState(false);
  const touchedFields = useRef(new Set<string>());
  const referenceEdited = useRef(false);
  const skipLookupFor = useRef<string | null>(null);
  const referenceFieldRef = useRef<HTMLDivElement>(null);

  const serialOptions = prefill.serialOptions ?? [];
  const attachments = prefill.attachments ?? [];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const generatedName = buildPassportName({
    brand,
    model,
    reference,
    dialColor: specs["Dial Color"],
    caseMaterial: specs["Case Material"],
    gender,
  });

  useEffect(() => {
    if (nameEdited) return;
    setName(generatedName);
  }, [generatedName, nameEdited]);

  useEffect(() => {
    const mintable = serialOptions.filter((s) => !s.minted);
    if (!prefill.serialNumber && mintable.length === 1) {
      setSerial(mintable[0].serialNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      for (const image of images) {
        if (image.kind === "upload") URL.revokeObjectURL(image.previewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!manualEntry) return;
    const t = setTimeout(() => setDebouncedReference(reference.trim()), 350);
    return () => clearTimeout(t);
  }, [manualEntry, reference]);

  useEffect(() => {
    if (!manualEntry || !referenceEdited.current) return;

    const normalized = normalizeReference(debouncedReference);
    if (skipLookupFor.current === normalized) {
      setWatchLoading(false);
      return;
    }

    if (normalized.length < MIN_REFERENCE_LENGTH) {
      setWatchMatches([]);
      setMatchesOpen(false);
      setWatchSearched(false);
      setWatchLoading(false);
      return;
    }

    let cancelled = false;
    setWatchLoading(true);
    setWatchError("");
    lookupWatchesByReference(debouncedReference)
      .then((found) => {
        if (cancelled) return;
        setWatchSearched(true);
        if (found.length === 1) {
          applyWatch(found[0], false);
          setWatchMatches([]);
          setMatchesOpen(false);
        } else {
          setWatchMatches(found);
          setMatchesOpen(found.length > 0);
          clearCatalogueFields();
        }
      })
      .catch((err: unknown) => {
        reportError(err);
        if (!cancelled) {
          setWatchMatches([]);
          setMatchesOpen(false);
          setWatchSearched(false);
          setWatchError(apiErrorMessage(err, "Catalogue lookup failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setWatchLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualEntry, debouncedReference]);

  useEffect(() => {
    if (!matchesOpen) return;

    function onPointerDown(event: PointerEvent) {
      if (!referenceFieldRef.current?.contains(event.target as Node)) {
        setMatchesOpen(false);
      }
    }

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [matchesOpen]);

  function clearCatalogueFields() {
    const owned = (field: string) => !touchedFields.current.has(field);

    if (owned("brand")) setBrand("");
    if (owned("model")) setModel("");
    setSpecs((prev) => ({
      ...prev,
      "Dial Color": owned("Dial Color") ? "" : prev["Dial Color"],
      "Case Material": owned("Case Material") ? "" : prev["Case Material"],
    }));
    setWatchNote("");
  }

  function applyWatch(watch: WatchSuggestion, overwrite: boolean) {
    if (overwrite) {
      for (const field of CATALOGUE_FIELDS) touchedFields.current.delete(field);
    }

    const owned = (field: string) => !touchedFields.current.has(field);
    const value = (next: string | null) => (next ?? "").trim();

    if (owned("brand")) setBrand(value(watch.brand));
    if (owned("model")) setModel(value(watch.model));
    setSpecs((prev) => ({
      ...prev,
      "Dial Color": owned("Dial Color")
        ? value(watch.dialColor)
        : prev["Dial Color"],
      "Case Material": owned("Case Material")
        ? value(watch.caseMaterial)
        : prev["Case Material"],
    }));
    setWatchNote(
      overwrite
        ? `Filled from ${watchTitle(watch)}`
        : `Matched ${watchTitle(watch)} — fields filled from the catalogue`
    );
  }

  function pickWatch(watch: WatchSuggestion) {
    if (watch.reference) {
      skipLookupFor.current = normalizeReference(watch.reference);
      setReference(watch.reference);
    }
    applyWatch(watch, true);
    setWatchMatches([]);
    setMatchesOpen(false);
  }

  function changeReference(value: string) {
    referenceEdited.current = true;
    skipLookupFor.current = null;
    setWatchNote("");
    setWatchError("");
    setWatchSearched(false);
    setWatchMatches([]);
    setMatchesOpen(false);
    setReference(value);
  }

  function editField(field: string, apply: () => void) {
    touchedFields.current.add(field);
    apply();
  }

  function addFromTicket(attachment: TicketAttachment) {
    setImages((prev) => [
      ...prev,
      { kind: "ticket", key: ticketKey(attachment), attachment },
    ]);
  }

  function addUploads(files: File[]) {
    if (files.length === 0) return;
    setImages((prev) => [
      ...prev,
      ...files.map((file, i) => ({
        kind: "upload" as const,
        key: `local:${Date.now()}:${i}:${file.name}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  }

  function dropImage(key: string) {
    setImages((prev) => {
      const target = prev.find((i) => i.key === key);
      if (target?.kind === "upload") URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.key !== key);
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setImages((prev) => {
      const from = prev.findIndex((i) => i.key === active.id);
      const to = prev.findIndex((i) => i.key === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to);
    });
  }

  const addedTicketKeys = new Set(
    images.filter((i) => i.kind === "ticket").map((i) => i.key)
  );
  const frontKey = images.length > 0 ? images[0].key : null;

  const trimmedSerial = serial.trim();
  const serialIsMinted = serialOptions.some(
    (s) => s.minted && s.serialNumber === trimmedSerial
  );

  const reopenMatches = !watchLoading && !matchesOpen && watchMatches.length > 0;
  const referenceStatus = watchLoading
    ? "Looking up reference…"
    : watchNote
      ? watchNote
      : watchSearched
        ? watchMatches.length === 0
          ? "No catalogue watch matches this reference"
          : ""
        : "Enter a reference to fill brand, model and specifications from the catalogue";

  const errors: Partial<Record<FieldKey, string>> = {};
  if (!brand.trim()) errors.brand = "Brand is required";
  if (!model.trim()) errors.model = "Model is required";
  if (!trimmedSerial) errors.serial = "Serial number is required";
  else if (serialIsMinted) {
    errors.serial = "A passport already exists for this serial number";
  }
  if (!department) errors.department = "Select a department";
  if (!eventDate) errors.eventDate = "Acquisition date is required";

  const shown: Partial<Record<FieldKey, string>> = submitAttempted
    ? errors
    : serialIsMinted
      ? { serial: errors.serial }
      : {};

  const messages = Object.values(errors).filter(
    (message): message is string => Boolean(message)
  );
  const validationSummary =
    submitAttempted && messages.length > 0
      ? messages.length === 1
        ? messages[0]
        : `${messages.length} fields need attention before this passport can be minted`
      : "";

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setSubmitAttempted(true);

    if (messages.length > 0) {
      return;
    }

    const uploads = images.filter(
      (i): i is Extract<PassportImage, { kind: "upload" }> =>
        i.kind === "upload"
    );
    const uploadIndex = new Map(uploads.map((u, i) => [u.key, i]));

    const fields: MintPassportFields = {
      brand: brand.trim(),
      model: model.trim(),
      serialNumber: trimmedSerial,
      department,
      eventDate,
    };

    if (reference.trim()) fields.reference = reference.trim();
    if (name.trim()) fields.name = name.trim();
    if (description.trim()) fields.description = description.trim();
    if (customerEmail.trim()) fields.customerEmail = customerEmail.trim();
    if (prefill.billNumber) fields.billNumber = prefill.billNumber;
    if (prefill.itemId) fields.itemId = prefill.itemId;

    const cleanAttributes = SPEC_FIELDS.map((spec) => ({
      type: spec.type as string,
      value: specs[spec.type].trim(),
    })).filter((spec) => spec.value);
    if (cleanAttributes.length > 0) fields.customAttributes = cleanAttributes;

    const ticketSelections = images.filter(
      (i): i is Extract<PassportImage, { kind: "ticket" }> =>
        i.kind === "ticket"
    );
    if (ticketSelections.length > 0) {
      fields.ticketAttachments = ticketSelections.map((i) => ({
        ticketId: i.attachment.ticketId,
        attachmentId: i.attachment.attachmentId,
      }));
    }

    if (images.length > 0) {
      fields.mediaOrder = images.map((image) =>
        image.kind === "ticket"
          ? image.key
          : `upload:${uploadIndex.get(image.key) ?? 0}`
      );
    }

    setSubmitting(true);
    try {
      await mintPassport(
        fields,
        uploads.map((u) => u.file)
      );
      setSubmitted(true);
      onMinted();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to mint passport"));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
        Mint queued. The passport will appear in Passports once it is minted on
        Arianee (this can take a couple of minutes).
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <div style={monoNote}>
        Fields marked <RequiredMark /> are required.
      </div>

      {prefill.sourceLabel && (
        <div
          style={{
            ...monoNote,
            border: "1px solid var(--line)",
            padding: "8px 12px",
          }}
        >
          Prefilled from{" "}
          <span style={{ color: "var(--ink)" }}>{prefill.sourceLabel}</span> —
          every field below can be changed before minting.
        </div>
      )}

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
      >
        <div className="field">
          <Label htmlFor="mint-brand">
            Brand
            <RequiredMark />
          </Label>
          <Input
            id="mint-brand"
            value={brand}
            placeholder="e.g. Rolex"
            onChange={(e) => editField("brand", () => setBrand(e.target.value))}
            disabled={submitting}
            aria-invalid={Boolean(shown.brand)}
          />
          <FieldError message={shown.brand} />
        </div>
        <div className="field">
          <Label htmlFor="mint-model">
            Model
            <RequiredMark />
          </Label>
          <Input
            id="mint-model"
            value={model}
            placeholder="e.g. Submariner Date"
            onChange={(e) => editField("model", () => setModel(e.target.value))}
            disabled={submitting}
            aria-invalid={Boolean(shown.model)}
          />
          <FieldError message={shown.model} />
        </div>
      </div>

      <div className="field">
        <Label htmlFor="mint-serial">
          Serial number
          <RequiredMark />
        </Label>
        <Input
          id="mint-serial"
          value={serial}
          placeholder="Enter serial number"
          onChange={(e) => setSerial(e.target.value)}
          disabled={submitting}
          aria-invalid={Boolean(shown.serial)}
          style={{ fontFamily: "var(--f-mono)" }}
        />
        <FieldError message={shown.serial} />
        {serialOptions.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                ...monoNote,
                fontSize: 10,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Serials on this source
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {serialOptions.map((s) => (
                <button
                  key={s.serialNumber}
                  type="button"
                  onClick={() => setSerial(s.serialNumber)}
                  disabled={submitting}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    background: "transparent",
                    border: `1px solid ${
                      serial === s.serialNumber
                        ? "var(--accent)"
                        : "var(--line)"
                    }`,
                    cursor: submitting ? "default" : "pointer",
                    fontFamily: "var(--f-mono)",
                    fontSize: 12,
                    color: "var(--ink)",
                  }}
                >
                  {s.serialNumber}
                  {s.minted && <Badge variant="success">Minted</Badge>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
      >
        <div
          className="field"
          style={{ position: "relative" }}
          ref={referenceFieldRef}
        >
          <Label htmlFor="mint-ref">Reference</Label>
          <Input
            id="mint-ref"
            value={reference}
            placeholder="e.g. 126610LN"
            autoComplete="off"
            onChange={(e) => changeReference(e.target.value)}
            onKeyDown={(e) => {
              if (!matchesOpen) return;
              if (e.key === "Escape") setMatchesOpen(false);
              if (e.key === "Enter") {
                e.preventDefault();
                setMatchesOpen(false);
              }
            }}
            disabled={submitting}
          />
          {manualEntry && watchError ? (
            <div style={{ ...monoNote, color: "var(--danger)" }}>
              {watchError}
            </div>
          ) : manualEntry && reopenMatches ? (
            <button
              type="button"
              onClick={() => setMatchesOpen(true)}
              disabled={submitting}
              style={{
                ...monoNote,
                alignSelf: "flex-start",
                background: "transparent",
                border: 0,
                cursor: submitting ? "default" : "pointer",
                padding: 0,
                textDecoration: "underline",
              }}
            >
              {watchMatches.length} catalogue watches match — show them
            </button>
          ) : (
            manualEntry &&
            referenceStatus && <div style={monoNote}>{referenceStatus}</div>
          )}
          {manualEntry && matchesOpen && watchMatches.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                zIndex: 20,
                minWidth: "100%",
                width: 420,
                maxWidth: "100vw",
                maxHeight: 260,
                overflowY: "auto",
                background: "var(--surface-3)",
                border: "1px solid var(--line-strong)",
                marginTop: 4,
              }}
            >
              <div
                style={{
                  ...monoNote,
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--line)",
                }}
              >
                {watchMatches.length} watches match this reference — pick one
              </div>
              {watchMatches.map((watch) => (
                <button
                  key={watch.id}
                  type="button"
                  onClick={() => pickWatch(watch)}
                  disabled={submitting}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "8px 12px",
                    background: "transparent",
                    border: 0,
                    borderBottom: "1px solid var(--line)",
                    textAlign: "left",
                    cursor: submitting ? "default" : "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--surface)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div style={{ fontSize: 13, color: "var(--ink)" }}>
                    {watchTitle(watch)}
                  </div>
                  {watchDetail(watch) && (
                    <div style={{ ...monoNote, marginTop: 2 }}>
                      {watchDetail(watch)}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="field">
          <Label htmlFor="mint-gender">Gender</Label>
          <select
            id="mint-gender"
            className="select"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            disabled={submitting}
            style={{ padding: "10px 14px", fontSize: 13, appearance: "none" }}
          >
            <option value="">Not specified</option>
            {GENDERS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <Label htmlFor="mint-name">Passport name</Label>
        <Input
          id="mint-name"
          value={name}
          placeholder="Shown as the passport title"
          onChange={(e) => {
            setNameEdited(true);
            setName(e.target.value);
          }}
          disabled={submitting}
        />
        {nameEdited ? (
          <button
            type="button"
            onClick={() => setNameEdited(false)}
            disabled={submitting}
            style={{
              ...monoNote,
              alignSelf: "flex-start",
              background: "transparent",
              border: 0,
              cursor: submitting ? "default" : "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Rebuild from the watch fields
          </button>
        ) : (
          <div style={monoNote}>
            Built from brand, model, reference, dial color, case material and
            gender — type here to write your own.
          </div>
        )}
      </div>

      <div className="field">
        <Label htmlFor="mint-desc">Description</Label>
        <Input
          id="mint-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="field">
        <Label>Watch specifications</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SPEC_FIELDS.map((spec) => (
            <div
              key={spec.type}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1.6fr",
                gap: 8,
                alignItems: "center",
              }}
            >
              <span style={monoNote}>{spec.type}</span>
              <Input
                placeholder={spec.placeholder}
                value={specs[spec.type]}
                onChange={(e) =>
                  editField(spec.type, () =>
                    setSpecs((prev) => ({
                      ...prev,
                      [spec.type]: e.target.value,
                    }))
                  )
                }
                disabled={submitting}
              />
            </div>
          ))}
        </div>
        <div style={{ ...monoNote, marginTop: 8, textTransform: "none" }}>
          These are the only specifications the passport shows — Arianee rejects
          anything else.
        </div>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
      >
        <div className="field">
          <Label htmlFor="mint-dept">
            Department
            <RequiredMark />
          </Label>
          <select
            id="mint-dept"
            className="select"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            disabled={submitting}
            aria-invalid={Boolean(shown.department)}
            style={{ padding: "10px 14px", fontSize: 13, appearance: "none" }}
          >
            <option value="" disabled>
              Select department…
            </option>
            {DEPARTMENTS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
          <FieldError message={shown.department} />
        </div>
        <div className="field">
          <Label htmlFor="mint-event-date">
            Acquisition date
            <RequiredMark />
          </Label>
          <Input
            id="mint-event-date"
            type="date"
            value={eventDate}
            max={today()}
            onChange={(e) => setEventDate(e.target.value)}
            disabled={submitting}
            aria-invalid={Boolean(shown.eventDate)}
          />
          <FieldError message={shown.eventDate} />
        </div>
      </div>

      <div
        style={{
          ...monoNote,
          border: "1px solid var(--line)",
          padding: "8px 12px",
        }}
      >
        Provenance event recorded on mint:{" "}
        <span style={{ color: "var(--ink)" }}>
          {department ? `“${acquisitionTitle(department)}”` : "—"}
        </span>
      </div>

      <div className="field">
        <Label htmlFor="mint-email">Customer email</Label>
        <Input
          id="mint-email"
          type="email"
          placeholder="optional"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          disabled={submitting}
        />
      </div>

      {images.length > 0 && (
        <div className="field">
          <Label>Passport images — drag to reorder, first is the front</Label>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((i) => i.key)}
              strategy={rectSortingStrategy}
            >
              <div style={gridStyle}>
                {images.map((image) => (
                  <SortableTile
                    key={image.key}
                    id={image.key}
                    dimmed={false}
                    hoverIcon="x"
                    badge={image.key === frontKey ? "Front" : undefined}
                    title="Click to discard"
                    onClick={() => dropImage(image.key)}
                  >
                    {image.kind === "upload" ? (
                      <img
                        src={image.previewUrl}
                        alt={image.file.name}
                        style={coverStyle}
                      />
                    ) : (
                      <RemoteThumbnail
                        alt={image.attachment.name}
                        load={() =>
                          fetchTicketThumbnail(
                            image.attachment.ticketId,
                            image.attachment.attachmentId
                          )
                        }
                      />
                    )}
                  </SortableTile>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="field">
          <Label>Ticket photos — click to add</Label>
          <div style={gridStyle}>
            {attachments.map((a) => {
              const key = ticketKey(a);
              const isAdded = addedTicketKeys.has(key);
              return (
                <Tile
                  key={key}
                  dimmed={isAdded}
                  inert={isAdded}
                  hoverIcon={isAdded ? "check" : "plus"}
                  title={
                    isAdded
                      ? `${a.name} — already added`
                      : `${a.name} — click to add`
                  }
                  onClick={() => addFromTicket(a)}
                >
                  <RemoteThumbnail
                    alt={a.name}
                    load={() => fetchTicketThumbnail(a.ticketId, a.attachmentId)}
                  />
                </Tile>
              );
            })}
          </div>
        </div>
      )}

      <div className="field">
        <Label>Upload images from this device</Label>
        <Dropzone
          accept="image/*"
          variant="image"
          disabled={submitting}
          files={[]}
          onFiles={addUploads}
          hint="PNG, JPG or HEIC"
        />
      </div>

      {(error || validationSummary) && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
          }}
        >
          {error || validationSummary}
        </div>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? "Minting…" : "Mint passport"}
      </Button>
    </form>
  );
}
